import {
  Alert,
  Box,
  Button,
  CssBaseline,
  FilledInput,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import AppBar from "./components/AppBar";
import CustomCircularProgress, {
  CustomCircularProgressFullScreen,
} from "../../components/CustomCircularProgress";
import { useEffect, useState } from "react";
import { MuiTelInput } from "mui-tel-input";
import { useOptionTree } from "./hooks/useOptionTree";
import AutoCompleteCustom from "../../components/AutoCompleteCustom";
import { useOptionTreeParameters } from "./hooks/useOptionTreeParameters";
import TreeViewCustom from "../../components/TreeViewCustom";
import { AccountTree, Image, Save } from "@mui/icons-material";
import { ITree } from "../../service/tpclient/interfaces/ITree";
import { useFormDialog } from "../../hooks/useFormDialog";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import ImageFormDialog from "./components/ImageFormDialog";
import { useOption } from "./hooks/useOption";
import { NumericFormatCustom } from "../../components/NumericFormatCustom";
import { usePageTitle } from "../../components/PageTitleProvider";
import { MuiFileInput } from "mui-file-input";
import { IExternal } from "../../service/external/interfaces/IExternal";
import { useExternalService } from "../../service/external";
import { useNotificationSnackbar } from "../../hooks/useNotificationSnackbar";
import { useValidateString } from "../../utils/useValidateString";
import { TextMaskCustom } from "../../components/TextMaskCustom";
import html2canvas from "html2canvas";
import numeroPedidoImg from "../../utils/assets/numero_pedido.gif";
import lojaImg from "../../utils/assets/loja.gif";

export interface EstornoPageProps {
  colorMode: any;
}

interface Errors {
  documento: string | null;
  nome: string | null;
  email: string | null;
  pedido: string | null;
  aconteceuPedido: string | null;
  telefone: string | null;
  dataPedido: string | null;
  numeroPedido: string | null;
  loja: string | null;
  descricaoPedido: string | null;
  valor: string | null;
  fotoPedido: string | null;
  notaPedido: string | null;
}

const EstornoPage: React.FC<EstornoPageProps> = ({ colorMode }) => {
  const { setPageTitle } = usePageTitle();
  const [loading, setLoading] = useState<boolean>(true);
  useEffect(() => {
    setPageTitle("Formulario de Estorno");
  }, [setPageTitle]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const externalModel: IExternal = {
    documento: "",
    nome: "",
    email: "",
    pedido: "",
    aconteceuPedido: "",
    telefone: "",
    dataPedido: dayjs().startOf("day"),
    numeroPedido: "",
    loja: "",
    descricaoPedido: "",
    valor: "",
    faltaTrocaPedido: "",
    fotoPedido: null,
    notaPedido: null,
    screenshot: null,
  };

  const initError: Errors = {
    documento: null,
    nome: null,
    email: null,
    pedido: null,
    aconteceuPedido: null,
    telefone: null,
    dataPedido: null,
    numeroPedido: null,
    loja: null,
    descricaoPedido: null,
    valor: null,
    fotoPedido: null,
    notaPedido: null,
  };
  const [model, setModel] = useState<IExternal>(externalModel);
  const [errors, setErrors] = useState<Errors>(initError);
  const [numberCase, setNumberCase] = useState<string>("");

  const [pedidos, pedidosLoading] = useOptionTree("PEREVI");
  const [lojas, lojasLoading] = useOption("LO3");
  const [aconteceuPedidos] = useOptionTreeParameters("OQACSP", model.pedido);
  const [saveButton, setSaveButton] = useState<boolean>(false);

  useEffect(() => {
    if (!pedidosLoading && !lojasLoading) {
      setLoading(false);
    }
  }, [pedidosLoading, lojasLoading]);

  const { saveForm } = useExternalService();
  const { onErrorSnackbar } = useNotificationSnackbar();
  const [classifyName, setClassifyName] = useState<string>("");
  const [treeData, setTreeData] = useState<ITree[]>([]);

  const [isOpenFormDialog, titleFormDialog, openFormDialog, closeFormDialog] =
    useFormDialog();

  const [
    isOpenImgFormDialog,
    titleImgFormDialog,
    openImgFormDialog,
    closeImgFormDialog,
  ] = useFormDialog();
  const [imgState, setImgState] = useState<any>();

  const clearError = () => {
    setErrors(initError);
  };
  const { validateEmail, validateTelefone } = useValidateString();

  const validFields = () => {
    const valid: boolean[] = [];

    if (!model.documento) {
      setErrors((prevState) => ({
        ...prevState,
        documento: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }

    if (!model.nome.trim()) {
      setErrors((prevState) => ({
        ...prevState,
        nome: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }
    if (!model.email.trim() || !validateEmail(model.email)) {
      setErrors((prevState) => ({
        ...prevState,
        email: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }

    const telefoneValidation = validateTelefone(model.telefone);   
    if (!telefoneValidation.valid) {
      setErrors((prevState) => ({
        ...prevState,
        telefone: telefoneValidation.message,
      }));
      valid.push(telefoneValidation.valid);
    }

    if (!model.pedido.trim()) {
      setErrors((prevState) => ({
        ...prevState,
        pedido: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }

    if (!model.aconteceuPedido.trim()) {
      setErrors((prevState) => ({
        ...prevState,
        aconteceuPedido: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }

    if (model.dataPedido === null) {
      setErrors((prevState) => ({
        ...prevState,
        dataPedido: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }

    if (model.numeroPedido.length < 20) {
      setErrors((prevState) => ({
        ...prevState,
        numeroPedido: "❌ Campo com mínimo de 20 caracteres",
      }));
      valid.push(false);
    }

    if (!model.numeroPedido.trim()) {
      setErrors((prevState) => ({
        ...prevState,
        numeroPedido: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }

    if (model.numeroPedido.length > 24) {
      setErrors((prevState) => ({
        ...prevState,
        numeroPedido: "❌ Campo com máximo de 24 caracteres",
      }));
      valid.push(false);
    }

    if (!model.loja.trim()) {
      setErrors((prevState) => ({
        ...prevState,
        loja: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }

    if (!model.descricaoPedido.trim()) {
      setErrors((prevState) => ({
        ...prevState,
        descricaoPedido: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }

    if (!model.valor.trim()) {
      setErrors((prevState) => ({
        ...prevState,
        valor: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }
    if (Number(model.valor) >= 1000) {
      setErrors((prevState) => ({
        ...prevState,
        valor: "❌ Excedeu limite do campo valor",
      }));
      valid.push(false);
    }

    if (!model.fotoPedido) {
      setErrors((prevState) => ({
        ...prevState,
        fotoPedido: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }
    if (!model.notaPedido) {
      setErrors((prevState) => ({
        ...prevState,
        notaPedido: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }

    return valid.length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearError();
    if (!validFields()) return;
    setSaveButton(true);
    const element = document.getElementById("Screenshot");
    if (element) {
      html2canvas(element).then((canvas) => {
        canvas.toBlob(async (blob) => {
          if (blob) {
            const file = new File([blob], "Formulario.png", {
              type: "image/png",
            });
            model.screenshot = file;
            setLoading(true);
            const response = await saveForm(model);
            if (response.status === 200) {
              setLoading(false);
              setSaveButton(false);
              setModel(externalModel);
              setNumberCase(response.data);
            } else {
              setLoading(false);
              setSaveButton(false);
              setModel(externalModel);
              onErrorSnackbar(response.data);
            }
          }
        });
      });
    } else {
      onErrorSnackbar("Screenshot");
      setSaveButton(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        overflowX: "hidden",
      }}
    >
      <CssBaseline />
      <AppBar />
      <Box
        sx={{
          flex: 1,
          mt: 8,
          pl: 2,
          pr: 2,
          pb: 2,
          overflowX: "hidden",
        }}
      >
        {loading && <CustomCircularProgressFullScreen />}
        {!numberCase ? (
          <Box
            component="form"
            id="Screenshot"
            noValidate
            onSubmit={handleSubmit}
          >
            <Grid container spacing={2}>
              <Grid xs={12} sm={12} md={12} lg={12} xl={12}>
                <Stack
                  direction="row"
                  alignContent="center"
                  justifyContent="center"
                >
                  <Typography
                    variant={isSmallScreen ? "subtitle1" : "h5"}
                    fontWeight={700}
                  >
                    FORMULÁRIO DE SOLICITAÇÃO DE ESTORNO
                  </Typography>
                </Stack>
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12} xl={12}>
                <Stack
                  direction="row"
                  alignContent="center"
                  justifyContent="start"
                >
                  <Typography variant={isSmallScreen ? "body2" : "body1"}>
                    Este formulário é destinado para clientes que tiveram
                    problemas em seus pedidos realizados via App do Méqui:
                    McDelivery, Peça e Retire ou Méqui Zap.
                  </Typography>
                </Stack>
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12} xl={12}>
                <Stack
                  direction="row"
                  alignContent="center"
                  justifyContent="start"
                >
                  <Typography variant={isSmallScreen ? "body2" : "body1"}>
                    Para problemas de pedidos realizados através do Ifood ou
                    Rappi, é necessário o contato direto com a plataforma
                    utilizada na compra.
                  </Typography>
                </Stack>
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <FormControl
                  variant="filled"
                  fullWidth
                  required
                  error={!!errors.documento}
                >
                  <InputLabel>CPF</InputLabel>
                  <FilledInput
                    value={model.documento}
                    onChange={(event) =>
                      setModel((prevState: any) => ({
                        ...prevState,
                        documento: event.target.value,
                      }))
                    }
                    inputComponent={TextMaskCustom as any}
                    inputProps={{
                      mask: "000.000.000-00", // Pass the mask as a prop to TextMaskCustom
                    }}
                  />
                  {!!errors.documento && (
                    <FormHelperText error={!!errors.documento}>
                      {errors.documento || ""}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <TextField
                  label="QUAL SEU NOME COMPLETO"
                  variant="filled"
                  value={model.nome}
                  onChange={(event) =>
                    setModel((prevState: any) => ({
                      ...prevState,
                      nome: event.target.value,
                    }))
                  }
                  fullWidth
                  required
                  error={!!errors.nome}
                  helperText={errors.nome || ""}
                />
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <TextField
                  label="QUAL O SEU E-MAIL DE CONTATO"
                  variant="filled"
                  value={model.email}
                  onChange={(event) =>
                    setModel((prevState: any) => ({
                      ...prevState,
                      email: event.target.value,
                    }))
                  }
                  fullWidth
                  required
                  error={!!errors.email}
                  helperText={errors.email || ""}
                />
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <MuiTelInput
                  label="QUAL O SEU TELEFONE PARA CONTATO"
                  variant="filled"
                  defaultCountry="BR"
                  value={model.telefone}
                  onChange={(value) =>
                    setModel((prevState: any) => ({
                      ...prevState,
                      telefone: value,
                    }))
                  }
                  fullWidth
                  required
                  error={!!errors.telefone}
                  helperText={errors.telefone || ""}
                />
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <FormControl fullWidth error={!!errors.pedido} required>
                  <FormLabel>
                    SEU PEDIDO FOI REALIZADO VIA MCDELIVERY, PEÇA E RETIRE OU
                    MÉQUI ZAP
                  </FormLabel>
                  <RadioGroup
                    value={model.pedido}
                    onChange={(event) =>
                      setModel((prevState: any) => ({
                        ...prevState,
                        pedido: event.target.value,
                      }))
                    }
                  >
                    {pedidosLoading && <CustomCircularProgress size={20} />}
                    {pedidos &&
                      pedidos?.options.map((item) => {
                        const label =
                          item.label.split(" – ")[
                            item.label.split(" - ").length
                          ];
                        return (
                          <FormControlLabel
                            key={item.idBRAN}
                            value={item.idBRAN}
                            control={<Radio />}
                            label={label ?? item.label}
                          />
                        );
                      })}
                  </RadioGroup>
                  {!!errors.pedido && (
                    <FormHelperText error={!!errors.pedido}>
                      {errors.pedido || ""}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              {aconteceuPedidos?.options && (
                <Grid xs={12} sm={12} md={12} lg={12}>
                  <AutoCompleteCustom
                    title="O QUE ACONTECEU COM SEU PEDIDO"
                    options={aconteceuPedidos?.options ?? []}
                    field="label"
                    fieldId="idBRAN"
                    value={model.aconteceuPedido}
                    onChange={(id) =>
                      setModel((prevState) => ({
                        ...prevState,
                        aconteceuPedido: id ?? "",
                      }))
                    }
                    variant="filled"
                    required
                    error={errors.aconteceuPedido}
                    startAdornment={
                      <Button
                        variant="text"
                        size="small"
                        aria-label="AccountTree"
                        onClick={() => {
                          setTreeData(aconteceuPedidos?.tree ?? []);
                          setClassifyName("aconteceuPedido");
                          openFormDialog("O QUE ACONTECEU COM SEU PEDIDO");
                        }}
                      >
                        <AccountTree />
                      </Button>
                    }
                  />
                </Grid>
              )}

              <Grid xs={12} sm={12} md={12} lg={12}>
                <DatePicker
                  label="EM QUE DATA O SEU PEDIDO FOI FEITO"
                  localeText={{
                    toolbarTitle: "EM QUE DATA O SEU PEDIDO FOI FEITO",
                    cancelButtonLabel: "CANCELAR",
                    okButtonLabel: "OK",
                  }}
                  value={model.dataPedido}
                  onChange={(newValue: any) => {
                    setModel((prevState) => ({
                      ...prevState,
                      dataPedido: newValue,
                    }));
                  }}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "filled",
                      error: !!errors.dataPedido,
                      helperText: errors.dataPedido || "",
                      required: true,
                    },
                  }}
                />
              </Grid>

              <Grid xs={12} sm={12} md={12} lg={12}>
                <Alert
                  severity="info"
                  sx={{ fontWeight: 700 }}
                  action={
                    <Button
                      size="small"
                      startIcon={<Image />}
                      onClick={() => {
                        setImgState(numeroPedidoImg);
                        openImgFormDialog("EXEMPLO DE NUMERAÇÃO DO PEDIDO");
                      }}
                    >
                      EXEMPLO
                    </Button>
                  }
                >
                  SEMPRE INFORMAR A NUMERAÇÃO COMPLETA DO PEDIDO
                </Alert>
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <TextField
                  label="QUAL É O NÚMERO DO SEU PEDIDO"
                  variant="filled"
                  value={model.numeroPedido}
                  onChange={(event) => {
                    if (
                      event.target.value.length >= 20 &&
                      event.target.value.length <= 24
                    ) {
                      setErrors((prevState: any) => ({
                        ...prevState,
                        numeroPedido: null,
                      }));
                    } else if (event.target.value.length < 20) {
                      setErrors((prevState: any) => ({
                        ...prevState,
                        numeroPedido: "❌ Campo com minímo de 20 caracteres",
                      }));
                    } else {
                      setErrors((prevState: any) => ({
                        ...prevState,
                        numeroPedido: "❌ Campo com máximo de 24 caracteres",
                      }));
                    }
                    setModel((prevState: any) => ({
                      ...prevState,
                      numeroPedido: event.target.value,
                    }));
                  }}
                  fullWidth
                  required
                  error={!!errors.numeroPedido}
                  helperText={errors.numeroPedido || ""}
                />
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <Alert
                  severity="info"
                  sx={{ fontWeight: 700 }}
                  action={
                    <Button
                      startIcon={<Image />}
                      size="small"
                      onClick={() => {
                        setImgState(lojaImg);
                        openImgFormDialog(
                          "EM QUAL LOJA O SEU PEDIDO FOI FEITO"
                        );
                      }}
                    >
                      Exemplo
                    </Button>
                  }
                >
                  EM QUAL LOJA O SEU PEDIDO FOI FEITO
                </Alert>
              </Grid>

              <Grid xs={12} sm={12} md={12} lg={12}>
                <AutoCompleteCustom
                  title="EM QUAL LOJA O SEU PEDIDO FOI FEITO"
                  options={lojas ?? []}
                  field="label"
                  fieldId="idBRAN"
                  value={model.loja}
                  onChange={(id) =>
                    setModel((prevState) => ({
                      ...prevState,
                      loja: id ?? "",
                    }))
                  }
                  variant="filled"
                  required
                  error={errors.loja}
                />
              </Grid>

              <Grid xs={12} sm={12} md={12} lg={12}>
                <TextField
                  label="NOS CONTE BREVEMENTE O QUE OCORREU COM O SEU PEDIDO"
                  variant="filled"
                  value={model.descricaoPedido}
                  onChange={(event) =>
                    setModel((prevState: any) => ({
                      ...prevState,
                      descricaoPedido: event.target.value,
                    }))
                  }
                  fullWidth
                  required
                  error={!!errors.descricaoPedido}
                  helperText={errors.descricaoPedido || ""}
                  multiline
                  rows={5}
                />
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <Alert severity="error" sx={{ fontWeight: 700 }}>
                  EM CASOS DE FALTA DE ITENS, INFORMAR APENAS O VALOR DO ITEM
                  FALTANTE.
                </Alert>
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <TextField
                  label="INFORME O VALOR A SER ESTORNADO"
                  variant="filled"
                  value={model.valor}
                  onChange={(event) => {
                    if (Number(event.target.value) < 1000) {
                      setErrors((prevState: any) => ({
                        ...prevState,

                        valor: null,
                      }));
                    } else {
                      setErrors((prevState: any) => ({
                        ...prevState,
                        valor: "❌ Excedeu limite do campo valor",
                      }));
                    }
                    setModel((prevState: any) => ({
                      ...prevState,
                      valor: event.target.value,
                    }));
                  }}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  fullWidth
                  required
                  error={!!errors.valor}
                  helperText={errors.valor || ""}
                />
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <Alert severity="warning" sx={{ fontWeight: 700 }}>
                  RESPONDER APENAS EM CASOS DE "FALTA DE ITENS/PRODUTOS
                  TROCADOS".
                </Alert>
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <TextField
                  label="QUAL ITEM FALTOU EM SEU PEDIDO OU FOI ENVIADO INCORRETAMENTE"
                  variant="filled"
                  value={model.faltaTrocaPedido}
                  onChange={(event) =>
                    setModel((prevState: any) => ({
                      ...prevState,
                      faltaTrocaPedido: event.target.value,
                    }))
                  }
                  fullWidth
                  multiline
                  rows={5}
                />
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <Alert severity="info" sx={{ fontWeight: 700 }}>
                SE VOCÊ RECEBEU O SEU PEDIDO, ANEXE UMA FOTO. CASO NÃO TENHA RECEBIDO ANEXE UM PRINT DO PEDIDO REALIZADO NO SEU APP.
                </Alert>
              </Grid>
              <Grid xs={12} md={12} lg={12} xl={12}>
                <MuiFileInput
                  label="ANEXE UMA FOTO DO SEU PEDIDO"
                  value={model.fotoPedido}
                  onChange={(newValue) => {
                    if (
                      newValue !== null &&
                      !newValue?.type.includes("image")
                    ) {
                      setErrors((prevState) => ({
                        ...prevState,
                        fotoPedido: "❌ Arquivo inválido",
                      }));
                      setModel((prevState: any) => ({
                        ...prevState,
                        fotoPedido: null,
                      }));
                    } else {
                      setErrors((prevState) => ({
                        ...prevState,
                        fotoPedido: null,
                      }));
                      setModel((prevState: any) => ({
                        ...prevState,
                        fotoPedido: newValue,
                      }));
                    }
                  }}
                  inputProps={{
                    accept: "image/*",
                  }}
                  fullWidth
                  error={!!errors.fotoPedido}
                  helperText={errors.fotoPedido || ""}
                />
              </Grid>
              <Grid xs={12} md={12} lg={12} xl={12}>
                <MuiFileInput
                  label="ANEXE UMA FOTO DA SUA NOTA FISCAL"
                  value={model.notaPedido}
                  onChange={(newValue) => {
                    if (
                      newValue !== null &&
                      !newValue?.type.includes("image")
                    ) {
                      setErrors((prevState) => ({
                        ...prevState,
                        notaPedido: "❌ Arquivo inválido",
                      }));
                      setModel((prevState: any) => ({
                        ...prevState,
                        notaPedido: null,
                      }));
                    } else {
                      setErrors((prevState) => ({
                        ...prevState,
                        notaPedido: null,
                      }));
                      setModel((prevState: any) => ({
                        ...prevState,
                        notaPedido: newValue,
                      }));
                    }
                  }}
                  inputProps={{
                    accept: "image/*",
                  }}
                  fullWidth
                  error={!!errors.notaPedido}
                  helperText={errors.notaPedido || ""}
                />
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <Button
                  startIcon={<Save />}
                  type="submit"
                  fullWidth
                  disabled={saveButton}
                >
                  Finalizar
                </Button>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid xs={12} sm={12} md={12} lg={12}>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                spacing={2}
              >
                <Typography variant="h3">😃</Typography>
                <Typography variant="subtitle1">
                  A avaliação do estorno acontecerá em até 7 dias úteis e você
                  receberá o retorno por e-mail.
                </Typography>
                <Typography variant="subtitle1">
                  O prazo de para pagamento ocorrerá de acordo com o método
                  utilizado na compra.
                </Typography>
                <Typography variant="subtitle1">
                  Número do Caso:
                  <Typography component="span" fontWeight={700}>
                    {" "}
                    {numberCase}
                  </Typography>
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        )}
        <ImageFormDialog
          isOpen={isOpenImgFormDialog}
          title={titleImgFormDialog}
          close={closeImgFormDialog}
          image={imgState}
        />
        <TreeViewCustom
          isOpen={isOpenFormDialog}
          title={titleFormDialog}
          close={closeFormDialog}
          treeData={treeData}
          setSelected={(idBran) => {
            setModel((prevState: any) => ({
              ...prevState,
              [classifyName]: idBran,
            }));
          }}
        />
      </Box>
    </Box>
  );
};

export default EstornoPage;
